'use client'

import { cn } from '@quadralize/utils'
import { CheckCircle } from '@solar-icons/react'
import { AnimatePresence, motion } from 'framer-motion'
import * as React from 'react'
import { ScrollArea } from '../../data-display/scroll-area/scroll-area'

// --- Context ---
interface StepperContextValue {
  activeStep: number
  setActiveStep: (step: number) => void
  orientation: 'horizontal' | 'vertical'
}

const StepperContext = React.createContext<StepperContextValue | null>(null)

function useStepper() {
  const context = React.useContext(StepperContext)
  if (!context) {
    throw new Error('useStepper must be used within a Stepper')
  }
  return context
}

// --- Components ---

interface StepperProps extends React.ComponentProps<'div'> {
  activeStep: number
  children: React.ReactNode
  orientation?: 'horizontal' | 'vertical'
  onStepClick?: (step: number) => void
}

const Stepper = React.forwardRef<HTMLDivElement, StepperProps>(
  (
    {
      activeStep: propsActiveStep,
      orientation = 'horizontal',
      children,
      className,
      onStepClick,
      ...props
    },
    ref,
  ) => {
    // We might want uncontrolled state if desired, but controlled is standard.
    // For now, we rely on the parent controlling it via props.
    // However, to make it flexible, let's just pass the setter if provided or no-op.

    const setActiveStep = React.useCallback(
      (step: number) => {
        onStepClick?.(step)
      },
      [onStepClick],
    )

    return (
      <StepperContext.Provider value={{ activeStep: propsActiveStep, setActiveStep, orientation }}>
        {orientation === 'horizontal' ? (
          <ScrollArea type="hover" scrollbarSize="sm" className={cn('w-full', className)}>
            <div
              ref={ref}
              className="flex gap-2 flex-row items-center whitespace-nowrap pb-3"
              {...props}
            >
              {React.Children.map(children, (child, index) => {
                if (React.isValidElement(child)) {
                  return React.cloneElement(child, {
                    // @ts-expect-error - Injecting index implicitly for cleaner API
                    index,
                    isLast: index === React.Children.count(children) - 1,
                  })
                }
                return child
              })}
            </div>
          </ScrollArea>
        ) : (
          <div ref={ref} className={cn('flex flex-col gap-2', className)} {...props}>
            {React.Children.map(children, (child, index) => {
              if (React.isValidElement(child)) {
                return React.cloneElement(child, {
                  // @ts-expect-error - Injecting index implicitly for cleaner API
                  index,
                  isLast: index === React.Children.count(children) - 1,
                })
              }
              return child
            })}
          </div>
        )}
      </StepperContext.Provider>
    )
  },
)
Stepper.displayName = 'Stepper'

interface StepProps extends React.ComponentProps<'div'> {
  index?: number // Injected
  isLast?: boolean // Injected
}

const Step = React.forwardRef<HTMLDivElement, StepProps>(
  ({ children, className, index = 0, isLast, ...props }, ref) => {
    const { activeStep, orientation } = useStepper()
    const status = activeStep === index ? 'active' : activeStep > index ? 'completed' : 'inactive'

    return (
      <div
        ref={ref}
        className={cn(
          'flex',
          orientation === 'horizontal' ? 'flex-1 items-center' : 'flex-col',
          !isLast && orientation === 'horizontal' ? 'w-full' : '',
          className,
        )}
        data-status={status}
        {...props}
      >
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, {
              // @ts-expect-error - Injecting props
              status,
              index,
              isLast,
            })
          }
          return child
        })}
      </div>
    )
  },
)
Step.displayName = 'Step'

interface StepTriggerProps extends React.ComponentProps<'button'> {
  status?: 'active' | 'completed' | 'inactive'
  index?: number
  onClick?: React.MouseEventHandler<HTMLButtonElement>
}

const StepTrigger = React.forwardRef<HTMLButtonElement, StepTriggerProps>(
  ({ children, className, status = 'inactive', index = 0, onClick, ...props }, ref) => {
    const { setActiveStep } = useStepper()

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (status !== 'inactive') {
        setActiveStep(index)
      }
      onClick?.(e)
    }

    return (
      <button
        ref={ref}
        type="button"
        onClick={handleClick}
        className={cn(
          'flex items-center gap-3 cursor-pointer group focus-visible:outline-none appearance-none bg-transparent border-0 p-0 text-left',
          className,
        )}
        {...props}
      >
        {/* Indicator */}
        <div
          className={cn(
            'flex items-center justify-center w-8 h-8 shrink-0 rounded-full border-2 transition-colors duration-300',
            status === 'active' && 'border-primary bg-primary text-white',
            status === 'completed' &&
              'border-primary bg-primary text-white group-hover:bg-primary/90',
            status === 'inactive' &&
              'border-border-default bg-bg-base text-text-tertiary group-hover:border-primary/50',
          )}
        >
          {status === 'completed' ? (
            <CheckCircle className="w-4 h-4" weight="Bold" />
          ) : (
            <span className="text-sm font-medium">{index + 1}</span>
          )}
        </div>

        {/* Content wrapper for horizontal text */}
        <div className="flex flex-col text-left max-md:hidden">{children}</div>
      </button>
    )
  },
)
StepTrigger.displayName = 'StepTrigger'

const StepTitle = React.forwardRef<HTMLHeadingElement, React.ComponentProps<'h3'>>(
  ({ className, children, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn('text-sm font-semibold text-text-primary leading-none', className)}
      {...props}
    >
      {children}
    </h3>
  ),
)
StepTitle.displayName = 'StepTitle'

const StepDescription = React.forwardRef<HTMLParagraphElement, React.ComponentProps<'p'>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn('text-xs text-text-secondary mt-1', className)} {...props} />
  ),
)
StepDescription.displayName = 'StepDescription'

const StepSeparator = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & { status?: 'active' | 'completed' | 'inactive' }
>(({ className, status, ...props }, ref) => {
  const { orientation } = useStepper()

  return (
    <div
      ref={ref}
      className={cn(
        'transition-colors duration-300 bg-border-default',
        status === 'completed' ? 'bg-primary' : 'bg-border-default',
        orientation === 'horizontal' ? 'h-[2px] w-full mx-4 flex-1' : 'w-[2px] h-full my-2 ml-4',
        className,
      )}
      {...props}
    />
  )
})
StepSeparator.displayName = 'StepSeparator'

const StepContent = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
  ({ className, children, ...props }, ref) => {
    // Example wrapper for content in vertical stepper,
    // or maybe simple padding in horizontal.
    const { orientation } = useStepper()

    if (orientation === 'horizontal') return null // Traditionally stepper content is separate in horizontal

    return (
      <div
        ref={ref}
        className={cn('ml-4 pl-8 pb-8 border-l-2 border-border-default', className)}
        {...props}
      >
        <AnimatePresence mode="popLayout">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </div>
    )
  },
)
StepContent.displayName = 'StepContent'

export { Step, StepContent, StepDescription, Stepper, StepSeparator, StepTitle, StepTrigger }
